import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from "react";
import { useAuthContext } from "@/context/AuthContext";
import { Program } from "@t3/shared";
import { programConverter } from "@/converters";
import {
  doc,
  getDoc,
  collectionGroup,
  where,
  getDocs,
  query,
  onSnapshot,
  collection,
  DocumentReference,
} from "firebase/firestore";
import { firestore } from "@/firebase";

interface ProgramContextType {
  program: Program;
  programs: Program[];
  loading: boolean;
  selectProgram: (programId: string) => Promise<Program>;
}

const ProgramContext = createContext<ProgramContextType>({
  program: {} as Program,
  programs: [],
  loading: true,
  selectProgram: async () => Promise.resolve({} as Program),
});

export function ProgramProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();
  const [program, setProgram] = useState<Program | null>(null);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentProgramSubscription, setCurrentProgramSubscription] = useState<{
    unsubscribe: () => void;
    ref: DocumentReference<Program>;
  } | null>(null);

  const userProgramsQuery = query(
    collectionGroup(firestore, "program-feature-users"),
    where("userId", "==", user.uid),
  );

  const programCollection = collection(
    firestore,
    `company/${user.company}/programs`,
  ).withConverter(programConverter);

  // Clean up subscription on unmount
  useEffect(() => {
    return () => {
      if (currentProgramSubscription) {
        setCurrentProgramSubscription((oldSub) => {
          if (oldSub) {
            oldSub.unsubscribe();
          }
          return null;
        });
      }
    };
  }, []);

  // Fetch available programs
  useEffect(() => {
    async function fetchPrograms() {
      try {
        // Get all programs where user is a member

        const userProgramsSnapshot = await getDocs(userProgramsQuery);

        console.log(
          "userProgramsSnapshot",
          userProgramsSnapshot.docs.map((doc) => doc.ref.path.split("/")[3]),
        );

        // Get unique program IDs and fetch full program details
        const programIds = Array.from(
          new Set(
            userProgramsSnapshot.docs.map((doc) => doc.ref.path.split("/")[3]),
          ),
        );

        console.log("programIds", programIds);

        const programs = await Promise.all(
          programIds.map(async (programId) => {
            const programDoc = await getDoc(doc(programCollection, programId));
            const programData = programDoc.data();
            if (!programData) {
              throw new Error(`Program data not found: ${programId}`);
            }
            return programData;
          }),
        );

        console.log("programs", programs);

        setPrograms(programs);

        // Auto-select single program
        if (programs.length > 0) {
          try {
            await selectProgram(programs[0].id);
          } catch (error) {
            console.error("Failed to auto-select program:", error);
          }
        } else if (programs.length === 0) {
          setProgram({} as Program);
          // throw new Error("No programs available for this user");
        }
      } catch (error) {
        console.error("Error fetching programs:", error);
        throw error;
      } finally {
        setLoading(false);
      }
    }

    fetchPrograms();
  }, [user.company, user.id]);

  const selectProgram = async (programId: string): Promise<Program> => {
    return new Promise<Program>((resolve, reject) => {
      try {
        setLoading(true);

        const programDocRef = doc(programCollection, programId).withConverter(
          programConverter,
        );

        const subscription = onSnapshot(
          programDocRef,
          (snapshot) => {
            const program = snapshot.data();
            if (!program) {
              setLoading(false);
              reject(`Program data not found: ${programId}`);
              return;
            }
            setProgram(program);
            setLoading(false);
            resolve(program);
          },
          (error) => {
            setLoading(false);
            reject(error);
          },
        );

        // Manage subscription
        setCurrentProgramSubscription((oldSub) => {
          if (oldSub) {
            oldSub.unsubscribe();
          }
          return {
            unsubscribe: subscription,
            ref: programDocRef,
          };
        });
      } catch (error) {
        setLoading(false);
        reject(error);
      }
    });
  };
  if (!program) {
    return <div>Loading...</div>;
  }

  return (
    <ProgramContext.Provider
      value={{
        program: program ?? ({} as Program),
        programs,
        loading,
        selectProgram,
      }}
    >
      {children}
    </ProgramContext.Provider>
  );
}

export function useProgramContext() {
  return useContext(ProgramContext);
}
